import React, { useState, useEffect } from "react";
import { handleApiError } from "../../../common/utils";
import { toast } from "react-toastify";
import { useOrgUsersStore } from "../../../store/store";
import { MultiSelectDropdown } from "../../../components/dropdown/MultiSelectDropdown";
import { updateIncident } from "../services/service";
import { transferIncidentToICS } from "../../ExternalEmergencyPreparedness/ICS/services/service";
import { ICSRoleType } from "../../ExternalEmergencyPreparedness/ICS/types/types";
import { usePermissions } from "../../../hooks/usePermission";
import { INCIDENTS_MODULE } from "../../../constants/modules";

interface EmergencyModalProps {
  data: any;
  onActionComplete?: () => void;
  mode?: "create" | "view" | "update";
  incidentId?: string;
  moduleKey?: string;
}

const EmergencyModal: React.FC<EmergencyModalProps> = ({
  data,
  onActionComplete,
  mode = "create",
  incidentId,
  moduleKey,
}) => {
  const { canUpdate, canCreate } = usePermissions();
  const hasUpdatePerm = canUpdate(moduleKey || INCIDENTS_MODULE);
  const hasCreatePerm = canCreate(moduleKey || INCIDENTS_MODULE);
  const hasActionPerm = hasUpdatePerm || hasCreatePerm;

  const [isEmergency, setIsEmergency] = useState(false);
  const [sendToICS, setSendToICS] = useState(false);
  const [icsRoles, setIcsRoles] = useState<
    { roleType: ICSRoleType; assignedUserId: string }[]
  >([]);
  const [selectedRoleType, setSelectedRoleType] = useState<ICSRoleType | "">(
    ""
  );
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();

  interface UserOption {
    id: string;
    name: string;
  }

  const icsRoleTypes: ICSRoleType[] = [
    "Incident Commander",
    "Operations Chief",
    "Safety Officer",
    "Communications Officer",
    "Planning Chief",
    "Logistics Chief",
    "Finance Chief",
  ];

  useEffect(() => {
    fetchOrgUsers();
  }, []);

  useEffect(() => {
    if (data?.criticalIncident || data?.isEmergency) {
      setIsEmergency(true);
    }
  }, [data]);

  const isDisabled = mode === "view" || !hasActionPerm;

  const handleAddRole = () => {
    if (!selectedRoleType || !selectedUserId) {
      toast.error("Please select both role type and user");
      return;
    }

    // Check if role type already assigned
    if (icsRoles.some((r) => r.roleType === selectedRoleType)) {
      toast.error("This role type is already assigned");
      return;
    }

    setIcsRoles([
      ...icsRoles,
      {
        roleType: selectedRoleType,
        assignedUserId: selectedUserId,
      },
    ]);

    setSelectedRoleType("");
    setSelectedUserId("");
  };

  const handleRemoveRole = (index: number) => {
    if (mode === "view") return;
    setIcsRoles(icsRoles.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!incidentId && !data?.id) {
      toast.error("Incident ID is required");
      return;
    }

    const targetIncidentId = incidentId || data?.id;

    try {
      setSubmitting(true);

      // First, mark incident as emergency if needed
      if (isEmergency) {
        await updateIncident(targetIncidentId, {
          isEmergency: true,
          criticalIncident: true,
        });
      }

      // If send to ICS is selected, transfer to ICS
      if (sendToICS && icsRoles.length > 0) {
        await transferIncidentToICS(
          targetIncidentId,
          icsRoles,
          data?.department ?? undefined
        );
        toast.success("Incident marked as emergency and transferred to ICS");
      } else if (isEmergency) {
        toast.success("Incident marked as emergency");
      }

      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      handleApiError(error, "Failed to process emergency action");
    } finally {
      setSubmitting(false);
    }
  };

  interface UserOption {
    id: string;
    name: string;
  }

  interface UserOption {
    id: string;
    name: string;
  }

  const userOptions: UserOption[] = orgUsersData.map((user: any): UserOption => ({
    id: user._id || user.id,
    name: `${user.name || user.userName || ""} - ${user.email || ""}`,
  }));

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="isEmergency"
            checked={isEmergency}
            onChange={(e) => {
              if (!isDisabled) setIsEmergency(e.target.checked);
            }}
            disabled={isDisabled}
            className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
          />
          <label
            htmlFor="isEmergency"
            className="text-lg font-semibold text-gray-900"
          >
            Mark as Emergency Incident
          </label>
        </div>

        {isEmergency && (
          <div className="ml-8 space-y-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="sendToICS"
                checked={sendToICS}
                onChange={(e) => {
                  if (!isDisabled) setSendToICS(e.target.checked);
                }}
                disabled={isDisabled}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="sendToICS"
                className="text-base font-medium text-gray-800"
              >
                Send to Incident Command System (ICS)
              </label>
            </div>

            {sendToICS && (
              <div className="mt-4 space-y-4">
                <h3 className="text-base font-semibold text-gray-900">
                  Assign ICS Roles
                </h3>

                {/* Role Assignment Form */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role Type
                    </label>
                    <select
                      value={selectedRoleType}
                      onChange={(e) =>
                        setSelectedRoleType(e.target.value as ICSRoleType)
                      }
                      disabled={isDisabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Role</option>
                      {icsRoleTypes
                        .filter(
                          (role) =>
                            !icsRoles.some((r) => r.roleType === role)
                        )
                        .map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assign To
                    </label>
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      disabled={isDisabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select User</option>
                      {userOptions.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleAddRole}
                  disabled={isDisabled || !selectedRoleType || !selectedUserId}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Add Role Assignment
                </button>

                {/* Assigned Roles List */}
                {icsRoles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Assigned Roles:
                    </h4>
                    <div className="space-y-2">
                      {icsRoles.map((role, index) => {
                        const user = userOptions.find(
                          (u: { id: string; name: string }) => u.id === role.assignedUserId
                        );
                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded"
                          >
                            <span className="text-sm">
                              <span className="font-medium">{role.roleType}</span>
                              {" - "}
                              <span className="text-gray-600">
                                {user?.name || "Unknown User"}
                              </span>
                            </span>
                            {!isDisabled && (
                              <button
                                type="button"
                                onClick={() => handleRemoveRole(index)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {sendToICS && icsRoles.length === 0 && (
                  <p className="text-sm text-amber-600 mt-2">
                    At least one ICS role must be assigned to transfer to ICS.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {!isDisabled && (
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => {
              if (onActionComplete) onActionComplete();
            }}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || (sendToICS && icsRoles.length === 0)}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {submitting ? "Processing..." : "Submit"}
          </button>
        </div>
      )}
    </div>
  );
};

export default EmergencyModal;
